import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";

import {
    FormDateRangePicker,
    FormDropDownList,
    FormAutoComplete,
    FormCheckbox,
} from "../../forms/form-components";

export const AdministratorVenueEventAlerts = () => {
    const dispatch = useDispatch();

    const formReference = useRef(null);

    const defaultTicketingWebsite = {
        ticketingWebsiteId: "",
        name: "All Websites",
    };
    const defaultVenueEventStatusType = {
        venueEventStatusTypeId: "",
        name: "All Statuses",
    };
    const defaultVenueEventAlertType = {
        venueEventAlertTypeId: "",
        name: "All Types",
    };
   
    const [ticketingWebsites, setTicketingWebsites] = useState([]);
    const [venueEventStatusTypes, setVenueEventStatusTypes] = useState([]);
    const [venueEventAlertTypes, setVenueEventAlertTypes] = useState([]);

    const [venueEventAlerts, setVenueEventAlerts] = useState(undefined);
    const [venueEventAlertCount, setVenueEventAlertCount] = useState(0);
    const [skipCount, setSkipCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(1);
    const [venues, setVenues] = useState([]);
    const [venueName, setVenueName] = useState(undefined);
    const [areVenuesLoading, setAreVenuesLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
    const [dataLoadingError, setDataLoadingError] = useState(undefined);
    const [searchCriteria, setSearchCriteria] = useState(undefined);
    const [lastSelectedVenueEventAlertKey, setLastSelectedVenueEventAlertKey] = useState(undefined);

    const autocompleteTimeout = useRef();
    const autocompleteTimeoutDelay = 500; //Milliseconds

    const SelectedCell = (props) => {
        const venueEventAlertKey = props.dataItem.venueEventAlertKey;
        const selected = props.dataItem.selected;

        return (
            <td className="text-center">
                <Checkbox onChange={(event) => selectedValueChanged(event, venueEventAlertKey, !selected)} value={selected} />
            </td>
        );
    };

    const SelectedHeaderCell = () => {
        return (
            <div className="text-center">
                <Checkbox onChange={headerCheckboxValueChanged} value={isHeaderCheckboxChecked} />
            </div>
        );
    };

    const ViewUrlCell = (props) => {
        const url = props.dataItem[props.field];

        return (
            <td className={"text-nowrap"}>
                {url ? <Link to={{ pathname: url }} target={"_blank"} rel={"noopener noreferrer"}>View Website</Link> : ""}
            </td>
        );
    };

    const GetTicketCountCell = (props) => {
        const key = props.dataItem.venueEventAlertKey;
        const currentTicketCount = props.dataItem.currentTicketCount;

        return (
            <td>
                {currentTicketCount !== undefined ? currentTicketCount : <Button fillMode="link" type={"button"} onClick={() => runAlert(key)}>Get</Button>}
            </td>
        );
    };

    const getTicketingWebsites = () => {
        const dataItem = {
            onlyActive: true,
        }

        fetch(`/api/general/ticketingwebsites`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                const allTicketingWebsites = [defaultTicketingWebsite].concat(result);
                setTicketingWebsites(allTicketingWebsites);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getVenueEventStatusTypes = () => {
        fetch(`/api/general/venueeventstatustypes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                const allVenueEventStatusTypes = [defaultVenueEventStatusType].concat(result);
                setVenueEventStatusTypes(allVenueEventStatusTypes);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getVenueEventAlertTypes = () => {
        fetch(`/api/general/venueeventalerttypes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                const allVenueEventAlertTypes = [defaultVenueEventAlertType].concat(result);
                setVenueEventAlertTypes(allVenueEventAlertTypes);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const venueChanged = (event) => {
        const value = event.value;

        clearTimeout(autocompleteTimeout.current);

        if (!value || value.length < 2) {
            setVenues([]);
            setAreVenuesLoading(false);
            setVenueName(null);
            return;
        }

        autocompleteTimeout.current = setTimeout(() => {
            const dataItem = {
                name: value,
                onlyActive: true,
            };

            fetch(`/api/administrator/venues`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        "X-XSRF-TOKEN": getCSRFToken(),
                    },
                    body: JSON.stringify(dataItem),
                })
                .then(handleResponse)
                .then(result => {
                    setVenues(result.venues);
                    setVenueName(value);
                })
                .catch(error => {
                    addNotificationWithTimeout(dispatch, "error", error.message);
                })
                .finally(() => {
                    setAreVenuesLoading(false);
                });
        }, autocompleteTimeoutDelay);

        setAreVenuesLoading(true);
    };

    const getVenueEventAlerts = (dataItem, newPageNumber) => {
        setIsDataLoading(true);

        if (!dataItem) {
            dataItem = searchCriteria;
        }

        dataItem.pageNumber = newPageNumber;

        fetch(`/api/administrator/report/venueeventalerts`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setItemsPerPage(result.venueEventAlertsPerPage);
                setSkipCount(result.venueEventAlertsPerPage * (result.pageNumber - 1));
                setVenueEventAlertCount(result.totalVenueEventAlertCount);

                let venueEventAlertsCopy = result.venueEventAlerts;

                venueEventAlertsCopy.forEach(dataItem =>
                    dataItem.selected = false
                );

                setVenueEventAlerts(venueEventAlertsCopy);

                setIsHeaderCheckboxChecked(false);
                setLastSelectedVenueEventAlertKey(undefined);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setDataLoadingError(error.message);
            })
            .finally(() => {
                setIsDataLoading(false);
            });
    };

    const getData = (dataItem) => {
        if (dataItem.onlyActive === undefined) {
            dataItem.onlyActive = false;
        }

        if (dataItem.venueEventAlertType !== undefined
            && dataItem.venueEventAlertType.value !== "") {
            dataItem.venueEventAlertTypeId = parseInt(dataItem.venueEventAlertType.venueEventAlertTypeId);
        }
        else {
            dataItem.venueEventAlertTypeId = undefined;
        }

        if (dataItem.ticketingWebsite !== undefined
            && dataItem.ticketingWebsite.value !== "") {
            dataItem.ticketingWebsiteId = parseInt(dataItem.ticketingWebsite.ticketingWebsiteId);
        }
        else {
            dataItem.ticketingWebsiteId = undefined;
        }

        if (dataItem.venueEventStatusType !== undefined
            && dataItem.venueEventStatusType.value !== "") {
            dataItem.venueEventStatusTypeId = parseInt(dataItem.venueEventStatusType.venueEventStatusTypeId);
        }
        else {
            dataItem.venueEventStatusTypeId = undefined;
        }

        dataItem.venueKey = undefined;
        if (venues.length > 0) {
            const venue = venues.find(element => element.currentName === venueName);

            if (venue) {
                dataItem.venueKey = venue.venueKey;
            }
        }

        dataItem.startingEventDate = undefined;
        dataItem.endingEventDate = undefined;
        if (dataItem.eventDates) {
            if (dataItem.eventDates.start) {
                dataItem.startingEventDate = dataItem.eventDates.start;
            }

            if (dataItem.eventDates.end) {
                dataItem.endingEventDate = dataItem.eventDates.end;
            }
        }

        setSearchCriteria(dataItem);
        getVenueEventAlerts(dataItem);
    }

    const pageChange = (event) => {
        const newPageNumber = (event.page.skip / event.page.take) + 1;
        getVenueEventAlerts(null, newPageNumber);
    };

    const selectedValueChanged = (event, venueEventAlertKey, selected) => {
        setIsDataLoading(true);

        setTimeout(() => {
            let venueEventAlertsCopy = venueEventAlerts;

            const itemIndex = venueEventAlertsCopy.findIndex(dataItem =>
                dataItem.venueEventAlertKey === venueEventAlertKey
            );

            let isSelectingMultipleItems = false;

            //Shift selection of multiple items            
            if (event.nativeEvent.shiftKey
                && lastSelectedVenueEventAlertKey !== undefined
                && lastSelectedVenueEventAlertKey !== venueEventAlertKey) {
                const lastSelectedItemIndex = venueEventAlertsCopy.findIndex(dataItem =>
                    dataItem.venueEventAlertKey === lastSelectedVenueEventAlertKey
                );

                if (lastSelectedItemIndex >= 0) {
                    isSelectingMultipleItems = true;

                    let lowIndex = -1;
                    let highIndex = -1;

                    if (itemIndex > lastSelectedItemIndex) {
                        lowIndex = lastSelectedItemIndex;
                        highIndex = itemIndex;
                    }
                    else {
                        lowIndex = itemIndex;
                        highIndex = lastSelectedItemIndex;
                    }

                    for (let i = lowIndex; i <= highIndex; i++) {
                        venueEventAlertsCopy[i].selected = selected;
                    }
                }
            }

            if (isSelectingMultipleItems === false) {
                venueEventAlertsCopy[itemIndex].selected = selected;
            }

            if (selected === true) {
                setLastSelectedVenueEventAlertKey(venueEventAlertKey);
            }

            setVenueEventAlerts(venueEventAlertsCopy);

            setIsHeaderCheckboxChecked(false);
            setIsDataLoading(false);
        }, 1);
    };

    const headerCheckboxValueChanged = () => {
        setIsDataLoading(true);

        setTimeout(() => {
            const checked = !isHeaderCheckboxChecked;
            setIsHeaderCheckboxChecked(checked);

            let venueEventAlertsCopy = venueEventAlerts;

            venueEventAlertsCopy.forEach(dataItem =>
                dataItem.selected = checked
            );

            setVenueEventAlerts(venueEventAlertsCopy);

            setLastSelectedVenueEventAlertKey(undefined);

            setIsDataLoading(false);
        }, 1);
    };

    const runSelectedAlerts = () => {
        let venueEventAlertKeys = [];

        venueEventAlerts.forEach(dataItem => {
            if (dataItem.selected === true) {
                venueEventAlertKeys.push(dataItem.venueEventAlertKey);
            }
        });

        if (venueEventAlertKeys.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No alerts selected");
            return;
        }

        runAlerts(venueEventAlertKeys);
    };

    const runAlert = (venueEventAlertKey) => {
        setIsDataLoading(true);

        const dataItem = {
            venueEventAlertKey: venueEventAlertKey
        }

        fetch("/api/administrator/ticketcount",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                let venueEventAlertsCopy = venueEventAlerts;
                const index = venueEventAlertsCopy.findIndex(x => x.venueEventAlertKey === venueEventAlertKey);
                venueEventAlertsCopy[index].currentTicketCount = result.ticketCount;

                setVenueEventAlerts(venueEventAlertsCopy);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsDataLoading(false);
            });
    };

    const runAlerts = (venueEventAlertKeys) => {
        if (window.confirm("Are you sure you want to run the selected alerts?") === false) {
            return;
        }

        setIsDataLoading(true);

        const dataItem = {
            venueEventAlertKeys: venueEventAlertKeys
        }

        fetch("/api/administrator/ticketcounts",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(results => {
                let venueEventAlertsCopy = venueEventAlerts;

                results.forEach(result => {
                    const index = venueEventAlertsCopy.findIndex(x => x.venueEventAlertKey === result.venueEventAlertKey);
                    venueEventAlertsCopy[index].currentTicketCount = result.ticketCount;
                });

                setVenueEventAlerts(venueEventAlertsCopy);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsDataLoading(false);
            });
    };

    useEffect(() => {
        document.title = "Event Alerts";

        getTicketingWebsites();
        getVenueEventStatusTypes();
        getVenueEventAlertTypes();
    }, []);

    return (
        <div className="administratorReport">
            <Form
                onSubmit={getData}
                ignoreModified={true}
                ref={formReference}
                render={(formRenderProps) => (
                    <FormElement>
                        <fieldset className={"k-form-fieldset container"}>
                            <div className="row">
                                <div className="col-12">
                                    <Field
                                        id={"onlyActive"}
                                        name={"onlyActive"}
                                        label={"Only active alerts"}
                                        component={FormCheckbox}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Field
                                        id={"venueEventAlertType"}
                                        name={"venueEventAlertType"}
                                        label={"Alert type"}
                                        component={FormDropDownList}
                                        data={venueEventAlertTypes}
                                        textField="name"
                                        dataItemKey="venueEventAlertTypeId"
                                        defaultValue={venueEventAlertTypes.find(element => element.venueEventAlertTypeId === "")}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Field
                                        id={"ticketingWebsite"}
                                        name={"ticketingWebsite"}
                                        label={"Ticketing website"}
                                        component={FormDropDownList}
                                        data={ticketingWebsites}
                                        textField="name"
                                        dataItemKey="ticketingWebsiteId"
                                        defaultValue={ticketingWebsites.find(element => element.ticketingWebsiteId === "")}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Field
                                        id={"venueEventStatusType"}
                                        name={"venueEventStatusType"}
                                        label={"Event status"}
                                        component={FormDropDownList}
                                        data={venueEventStatusTypes}
                                        textField="name"
                                        dataItemKey="venueEventStatusTypeId"
                                        defaultValue={venueEventStatusTypes.find(element => element.venueEventStatusTypeId === "")}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <Field
                                        id={"venueName"}
                                        name={"venueName"}
                                        label={"Venue"}
                                        component={FormAutoComplete}
                                        data={venues}
                                        placeholder="e.g. Roseland"
                                        textField="currentName"
                                        onChange={venueChanged}
                                        loading={areVenuesLoading}
                                    />
                                </div>                                
                                <div className="col-md-6 col-lg-4">
                                    <Field
                                        id={"eventDates"}
                                        name={"eventDates"}
                                        component={FormDateRangePicker}
                                        format={"MMM dd, yyyy"}
                                        startDateInputSettings={{
                                            label: "Starting event date"
                                        }}
                                        endDateInputSettings={{
                                            label: "Ending event date"
                                        }}
                                        calendarSettings={{
                                            views: 1
                                        }}
                                    />
                                </div>
                                <div className="col-lg-2">
                                    <div className="k-form-field">
                                        <label className="k-label">&nbsp;</label>
                                        <Button
                                            type={"submit"}
                                            themeColor={"primary"}
                                            icon="search"
                                        >
                                            Search
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    </FormElement>
                )}
            />

            {isDataLoading && <LoadingPanel />}

            {dataLoadingError &&
                <div>
                    {dataLoadingError}
                </div>
            }

            {venueEventAlerts &&
                <>
                    <div className="ms-2 mb-2">
                        <Button type={"button"} icon="close" title="Run Selected Alerts" onClick={runSelectedAlerts}>
                            Run Selected Alerts
                        </Button>
                    </div>  

                    <Grid
                        data={venueEventAlerts}
                        resizable={true}
                        skip={skipCount}
                        take={itemsPerPage}
                        total={venueEventAlertCount}
                        pageable={true}
                        onPageChange={pageChange}
                        scrollable={"none"}
                        dataItemKey="venueEventAlertKey"
                    >                  
                        <Column cell={SelectedCell} headerCell={SelectedHeaderCell} />
                        <Column field="ticketingUrl" title={"Website"} cell={ViewUrlCell} />
                        <Column title={"Count"} cell={GetTicketCountCell} />
                        <Column field="eventName" title={"Event"} />
                        <Column field="venueName" title={"Venue"} />
                        <Column field="eventDate" title={"Event Date"} />
                        <Column field="venueEventStatusTypeName" title={"Event Status"} />
                        <Column field="sectionName" title={"Section"} />
                        <Column field="venueEventAlertTypeName" title={"Type"} />                    
                    </Grid>
                </>
            }
        </div>
    );
}